<template>
  <b-modal
    v-model="showModal"
    size="xl"
    id="sale-shippings-modal"
    :title="sale ? `Shippings for order #${sale.id}` : 'Shippings'"
    hide-footer
  >
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h5 class="mb-0">&nbsp;</h5>
      <b-button
        v-if="allowChange && canCreateShipping"
        variant="primary"
        size="sm"
        @click="emitCreate"
      >
        Create Shipping
      </b-button>
    </div>

    <div v-if="isLoading" class="text-center py-3">
      <b-spinner label="Loading..." />
    </div>

    <div v-else-if="shippings.length">
      <b-table
        striped
        hover
        class="sale-shippings-table"
        :items="shippings"
        :fields="tableFields"
      >
        <template #cell(tracking_id)="data">
          <a
            v-if="buildTrackingHref(data.item.tracking_url, data.item.tracking_id)"
            :href="buildTrackingHref(data.item.tracking_url, data.item.tracking_id)"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ data.item.tracking_id || 'Track shipment' }}
          </a>
          <span v-else>{{ data.item.tracking_id || '-' }}</span>
        </template>

        <template #cell(shipping_date)="data">
          {{ formatShippingDate(data.item.shipping_date) }}
        </template>

        <template #cell(units)="data">
          {{ formatUnits(data.item.units) }}
        </template>

        <template #cell(actions)="data">
          <span v-if="allowChange" class="action-link-group">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="emitEdit(data.item.id)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>View / Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDelete(data.item)">
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
      </b-table>
    </div>

    <div v-else class="text-center py-3">
      <p class="mb-2">No shippings yet.</p>
      <b-button
        v-if="allowChange && canCreateShipping"
        variant="primary"
        @click="emitCreate"
      >
        Create Shipping
      </b-button>
    </div>

    <div class="text-right mt-2">
      <b-button variant="outline-secondary" @click="showModal = false">
        Close
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { buildTrackingHref } from '@/libs/utils'
import { formatCompanyDate } from '@/libs/companyTime'

export default {
  methods: {
    buildTrackingHref,
    formatShippingDate(value) {
      return formatCompanyDate(value) || value || '-'
    },
    formatUnits(units) {
      if (!Array.isArray(units) || !units.length) {
        return '-'
      }

      return units.map((unit) => `${unit.name} x${unit.quantity}`).join(', ')
    },
  },
}
</script>

<script setup>
import { ref, watch, computed } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { getCurrentInstance } from 'vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'

const props = defineProps({
  sale: {
    type: Object,
    default: null,
  },
  clickDate: {
    type: Date,
    required: true,
  },
  allowChange: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['create', 'edit', 'remaining-changed'])

const store = useStore()
const instance = getCurrentInstance()
const showModal = ref(false)
const isLoading = ref(false)
const shippings = ref([])
const hasRemainingToShip = ref(false)

const isDeliveryOrder = computed(() => {
  if (!props.sale) {
    return false
  }
  return !(props.sale.pickup === true || props.sale.pickup === 1)
})

const canCreateShipping = computed(() => {
  return isDeliveryOrder.value && (hasRemainingToShip.value || shippings.value.length === 0)
})

const tableFields = [
  { key: 'carrier_name', label: 'Carrier name' },
  { key: 'tracking_id', label: 'Tracking ID' },
  { key: 'shipping_date', label: 'Shipping date' },
  { key: 'units', label: 'Shipped units' },
  { key: 'actions', label: 'Actions', class: 'text-center', thClass: 'text-center', tdClass: 'text-center' },
]

const updateRemainingToShip = (context, shippingList) => {
  if (shippingList.length === 0 && isDeliveryOrder.value) {
    hasRemainingToShip.value = true
  } else {
    hasRemainingToShip.value = !!(context && context.has_remaining_to_ship)
  }

  const hasAnyShipped = shippingList.length > 0 || !!(context && context.has_any_shipped)

  if (props.sale) {
    props.sale.has_remaining_to_ship = hasRemainingToShip.value
    props.sale.has_any_shipped = hasAnyShipped
  }

  emit('remaining-changed', {
    has_remaining_to_ship: hasRemainingToShip.value,
    has_any_shipped: hasAnyShipped,
  })
}

const syncRemainingToShip = async () => {
  if (!props.sale || !props.sale.id) {
    return null
  }

  return store.dispatch('shippings/loadShippingContext', {
    saleId: props.sale.id,
  })
}

const loadShippings = async () => {
  if (!props.sale || !props.sale.id) {
    return
  }

  isLoading.value = true
  try {
    const context = await syncRemainingToShip()
    shippings.value = await store.dispatch('shippings/loadBySale', props.sale.id)
    updateRemainingToShip(context, shippings.value)
    showModal.value = true
  } catch (error) {
    showModal.value = false
    toastFailure(backendErrorString(error))
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.clickDate,
  () => {
    if (!props.sale) {
      return
    }
    loadShippings()
  },
)

const refresh = async () => {
  if (!props.sale || !props.sale.id) {
    return
  }

  const context = await syncRemainingToShip()
  shippings.value = await store.dispatch('shippings/loadBySale', props.sale.id)
  updateRemainingToShip(context, shippings.value)
}

defineExpose({ refresh })

const emitCreate = () => {
  emit('create', props.sale)
}

const emitEdit = (shippingId) => {
  emit('edit', { sale: props.sale, shippingId })
}

const confirmDelete = async (shipping) => {
  const confirmed = await instance.proxy.$bvModal.msgBoxConfirm(
    `Delete shipping from ${shipping.carrier_name}?`,
    {
      title: 'Please Confirm',
      okVariant: 'danger',
      okTitle: 'Delete',
      cancelTitle: 'Cancel',
    },
  )

  if (!confirmed) {
    return
  }

  try {
    await store.dispatch('shippings/deleteShipping', shipping.id)
    toastSuccess('Shipping deleted successfully.')
    await refresh()
  } catch (error) {
    toastFailure(backendErrorString(error))
  }
}
</script>

<style lang="scss" scoped>
/*
 * Keep first column (Carrier) and last (Actions); progressively
 * hide middle columns as the viewport narrows.
 */
@mixin hide-middle-from($start) {
  :deep(thead > tr > th:nth-child(n + #{$start}):not(:last-child)),
  :deep(tbody > tr > td:nth-child(n + #{$start}):not(:last-child)) {
    display: none !important;
  }
}

.sale-shippings-table {
  @media (max-width: 991.98px) {
    @include hide-middle-from(4);
  }

  @media (max-width: 767.98px) {
    @include hide-middle-from(3);
  }

  @media (max-width: 575.98px) {
    @include hide-middle-from(2);
  }
}
</style>
